/** Shared PCM sample rate for walkie-talkie streaming (voice-optimized). */
export const PTT_SAMPLE_RATE = 16000;

/** Max hold duration before auto-ending a PTT session. */
export const PTT_MAX_DURATION_MS = 30_000;

export function float32ToInt16Base64(samples: Float32Array): string {
  const int16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(int16.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function int16Base64ToFloat32(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float32;
}

/** Schedules incoming PCM chunks for gapless playback. */
export class PttPlaybackQueue {
  private ctx: AudioContext | null = null;
  private nextTime = 0;
  private sampleRate = PTT_SAMPLE_RATE;

  async ensureContext(sampleRate = PTT_SAMPLE_RATE): Promise<AudioContext> {
    this.sampleRate = sampleRate;
    if (!this.ctx) {
      this.ctx = new AudioContext({ sampleRate });
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  async playChunk(base64: string): Promise<void> {
    const ctx = await this.ensureContext(this.sampleRate);
    const samples = int16Base64ToFloat32(base64);
    if (samples.length === 0) return;

    const buffer = ctx.createBuffer(1, samples.length, this.sampleRate);
    // getChannelData().set() avoids Float32Array<ArrayBufferLike> vs ArrayBuffer TS mismatch
    buffer.getChannelData(0).set(samples);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now + 0.02, this.nextTime);
    source.start(startAt);
    this.nextTime = startAt + buffer.duration;
  }

  reset(): void {
    this.nextTime = 0;
  }

  close(): void {
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.nextTime = 0;
  }
}

export type PttCaptureHandle = {
  stop: () => void;
};

/**
 * Captures microphone PCM via ScriptProcessor and invokes onChunk with base64 Int16 payloads.
 */
export async function startPttCapture(
  onChunk: (base64: string) => void
): Promise<PttCaptureHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });

  const ctx = new AudioContext({ sampleRate: PTT_SAMPLE_RATE });
  await ctx.resume().catch(() => {});

  const source = ctx.createMediaStreamSource(stream);
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    onChunk(float32ToInt16Base64(input));
  };

  const silentGain = ctx.createGain();
  silentGain.gain.value = 0;
  source.connect(processor);
  processor.connect(silentGain);
  silentGain.connect(ctx.destination);

  return {
    stop: () => {
      processor.onaudioprocess = null;
      processor.disconnect();
      source.disconnect();
      stream.getTracks().forEach((t) => t.stop());
      void ctx.close().catch(() => {});
    },
  };
}
